import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from '../lib/cn.js';

// The one pill/badge component. Models the ratified booking-state pattern
// (`bg-<token>/15 text-<token>`) and replaces every inline status chip across
// the dashboard (capacity, activity-meta, deposit, "no reservable", …).
//
// Tones map 1:1 to the ratified Cálido semantic roles — there is NO red in
// Nuberu; destructive/warning is dark amber (`status-warning`):
//   success   → verde        (paid, available)
//   warning   → ámbar oscuro (destructive-but-done, "no reservable")
//   attention → terracota    (urgent, deposit, safety avisos)
//   info      → teal family  (informational)
//   primary   → teal         (brand, refunded)
//   neutral   → parchment    (muted/"oculta", inert)
//   full      → clay         (full / cancelled / down-trend)
//
// NOTE (warm-tone dedup, tracked): `full` (clay #cf553e) and `attention`
// (terracotta #dd5f48) sit ~1 hue apart. They are kept distinct on purpose so
// "full/cancelled" and "urgent" stop being used interchangeably; a future
// decision may merge them. Do not collapse without that decision.
const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full leading-[1.4] whitespace-nowrap',
  {
    variants: {
      tone: {
        success: 'bg-status-success/15 text-status-success',
        warning: 'bg-status-warning/15 text-status-warning',
        attention: 'bg-attention/15 text-attention-text',
        info: 'bg-status-info/15 text-status-info',
        primary: 'bg-primary/15 text-primary',
        neutral: 'bg-surface-4 text-text-secondary',
        full: 'bg-accent-clay/15 text-accent-clay',
        // Filled teal — for counters and "Lleno"/at-capacity chips that read as
        // a solid pill rather than a tint.
        solid: 'bg-primary text-primary-contrast',
      },
      // `sm` is the micro pill for dense surfaces (capacity, calendar,
      // activity-meta); `md` is the default. Casing is orthogonal — set
      // `uppercase` for the SHOUTY tag look (capacity "Lleno", booking states),
      // leave it off for sentence-case labels ("No reservable", "Oculta").
      size: {
        sm: 'px-2 py-0.5 text-2xs',
        md: 'px-2.5 py-0.5 text-xs',
      },
      uppercase: {
        true: 'font-bold uppercase tracking-wide',
        false: 'font-semibold',
      },
    },
    defaultVariants: {
      tone: 'neutral',
      size: 'md',
      uppercase: false,
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

export const Badge = ({
  className,
  tone,
  size,
  uppercase,
  asChild = false,
  ...props
}: BadgeProps): ReactElement => {
  const Comp = asChild ? Slot : 'span';
  return <Comp className={cn(badgeVariants({ tone, size, uppercase }), className)} {...props} />;
};

export { badgeVariants };
